import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Users, Clock, CreditCard, Banknote, Loader, CheckCircle } from 'lucide-react';
import { bookingService } from '../../services/api';
import PayPalButton from '../../components/PayPalButton';
import toast from 'react-hot-toast';

interface Booking {
  id: number;
  code_reservation: string;
  date_debut: string;
  date_fin: string;
  nb_personnes: number;
  nb_nuits: number;
  montant_total: number;
  prix_par_nuit: number;
  frais_menage: number;
  status: string;
  payment_status: string;
  mode_paiement: string | null;
  apartment?: {
    id: number;
    titre: string;
    ville: string;
    adresse: string;
  };
}

const BookingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<string>('');
  const [showPayPalButton, setShowPayPalButton] = useState(false);

  const fetchBooking = useCallback(async () => {
    if (!id) return;
    try {
      const response = await bookingService.getById(parseInt(id));
      setBooking(response.data.booking);
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBooking();
    
    // Vérifier si c'est un retour PayPal
    const paypalStatus = searchParams.get('paypal');
    if (paypalStatus === 'success') {
      toast.success('Paiement PayPal en cours de vérification...');
    } else if (paypalStatus === 'cancel') {
      toast('Paiement PayPal annulé', { icon: '⚠️' });
    }
  }, [fetchBooking, searchParams]);

  const handlePayment = async () => {
    if (!booking || !selectedPayment) {
      toast.error('Veuillez sélectionner un mode de paiement');
      return;
    }

    // Pour PayPal, afficher le bouton PayPal
    if (selectedPayment === 'paypal') {
      setShowPayPalButton(true);
      return;
    }

    setPaying(true);
    try {
      await bookingService.pay(booking.id, selectedPayment);
      
      if (selectedPayment === 'cash') {
        toast.success('Demande de paiement en espèces envoyée ! Le gérant validera votre paiement.');
      } else {
        toast.success('Paiement effectué avec succès !');
      }
      
      fetchBooking(); // Refresh
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors du paiement');
    } finally {
      setPaying(false);
    }
  };

  const handlePayPalSuccess = () => {
    setShowPayPalButton(false);
    fetchBooking();
  };

  const handlePayPalCancel = () => {
    setShowPayPalButton(false);
  };

  const formatPrice = (price: number) => new Intl.NumberFormat('fr-FR').format(price);
  const formatDate = (date: string) => new Date(date).toLocaleDateString('fr-FR', { 
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
  });

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { label: string; className: string }> = {
      pending: { label: 'En attente de paiement', className: 'bg-yellow-100 text-yellow-800' },
      confirmed: { label: 'Confirmée', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Annulée', className: 'bg-red-100 text-red-800' },
      completed: { label: 'Terminée', className: 'bg-neutral-100 text-neutral-800' },
    };
    const badge = badges[status] || { label: status, className: 'bg-neutral-100' };
    return <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.className}`}>{badge.label}</span>;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="text-center py-12">
        <p className="text-neutral-500">Réservation non trouvée</p>
        <Link to="/locataire/bookings" className="btn btn-primary mt-4">Retour</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link to="/locataire/bookings" className="inline-flex items-center text-neutral-600 hover:text-primary-600">
        <ArrowLeft className="w-4 h-4 mr-2" />Retour aux réservations
      </Link>

      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Réservation {booking.code_reservation}</h1>
          {booking.apartment && (
            <p className="text-neutral-600">{booking.apartment.titre}</p>
          )}
        </div>
        {getStatusBadge(booking.status)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Détails */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4">Détails du séjour</h2>
            
            {booking.apartment && (
              <div className="flex items-start gap-3 mb-4 pb-4 border-b">
                <MapPin className="w-5 h-5 text-primary-500 mt-0.5" />
                <div>
                  <p className="font-medium">{booking.apartment.titre}</p>
                  <p className="text-sm text-neutral-500">{booking.apartment.adresse}, {booking.apartment.ville}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-primary-500" />
                <div>
                  <p className="text-sm text-neutral-500">Arrivée</p>
                  <p className="font-medium">{formatDate(booking.date_debut)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-primary-500" />
                <div>
                  <p className="text-sm text-neutral-500">Départ</p>
                  <p className="font-medium">{formatDate(booking.date_fin)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-primary-500" />
                <div>
                  <p className="text-sm text-neutral-500">Durée</p>
                  <p className="font-medium">{booking.nb_nuits} nuit{booking.nb_nuits > 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-primary-500" />
                <div>
                  <p className="text-sm text-neutral-500">Voyageurs</p>
                  <p className="font-medium">{booking.nb_personnes} personne{booking.nb_personnes > 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Paiement - masquer si le locataire a déjà demandé un paiement en espèces */}
          {booking.status === 'pending' && booking.mode_paiement !== 'cash' && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">Mode de paiement</h2>
              
              <div className="space-y-3">
                <label className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition ${
                  selectedPayment === 'wave' ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 hover:border-neutral-300'
                }`}>
                  <input
                    type="radio"
                    name="payment"
                    value="wave"
                    checked={selectedPayment === 'wave'}
                    onChange={(e) => setSelectedPayment(e.target.value)}
                    className="w-4 h-4 text-primary-600"
                  />
                  <CreditCard className="w-6 h-6 text-blue-500" />
                  <div className="flex-1">
                    <p className="font-medium">Wave</p>
                    <p className="text-sm text-neutral-500">Paiement mobile sécurisé</p>
                  </div>
                </label>

                <label className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition ${
                  selectedPayment === 'paypal' ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 hover:border-neutral-300'
                }`}>
                  <input
                    type="radio"
                    name="payment"
                    value="paypal"
                    checked={selectedPayment === 'paypal'}
                    onChange={(e) => setSelectedPayment(e.target.value)}
                    className="w-4 h-4 text-primary-600"
                  />
                  <CreditCard className="w-6 h-6 text-blue-600" />
                  <div className="flex-1">
                    <p className="font-medium">PayPal</p>
                    <p className="text-sm text-neutral-500">Paiement sécurisé via PayPal</p>
                  </div>
                </label>

                <label className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition ${
                  selectedPayment === 'cash' ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 hover:border-neutral-300'
                }`}>
                  <input
                    type="radio"
                    name="payment"
                    value="cash"
                    checked={selectedPayment === 'cash'}
                    onChange={(e) => setSelectedPayment(e.target.value)}
                    className="w-4 h-4 text-primary-600"
                  />
                  <Banknote className="w-6 h-6 text-green-500" />
                  <div className="flex-1">
                    <p className="font-medium">Espèces</p>
                    <p className="text-sm text-neutral-500">Paiement en main propre au gérant</p>
                  </div>
                </label>
              </div>

              {selectedPayment === 'cash' && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 text-sm">
                    <strong>Paiement en espèces :</strong> Contactez le gérant pour convenir d'un rendez-vous. 
                    Votre réservation sera confirmée une fois le paiement validé par le gérant.
                  </p>
                </div>
              )}

              {/* Bouton PayPal */}
              {showPayPalButton && selectedPayment === 'paypal' ? (
                <div className="mt-6">
                  <PayPalButton
                    bookingId={booking.id}
                    amount={booking.montant_total}
                    onSuccess={handlePayPalSuccess}
                    onCancel={handlePayPalCancel}
                  />
                  <button
                    onClick={() => setShowPayPalButton(false)}
                    className="btn btn-outline w-full mt-3"
                  >
                    Annuler
                  </button>
                </div>
              ) : (
                <button
                  onClick={handlePayment}
                  disabled={!selectedPayment || paying}
                  className="btn btn-primary w-full mt-6"
                >
                  {paying ? (
                    <><Loader className="w-4 h-4 mr-2 animate-spin" />Traitement...</>
                  ) : selectedPayment === 'cash' ? (
                    'Demander le paiement en espèces'
                  ) : selectedPayment === 'paypal' ? (
                    'Payer avec PayPal'
                  ) : (
                    `Payer ${formatPrice(booking.montant_total)} FCFA`
                  )}
                </button>
              )}
            </div>
          )}

          {booking.status === 'pending' && booking.mode_paiement === 'cash' && (
            <div className="card p-6 bg-orange-50 border-orange-200">
              <div className="flex items-center gap-3">
                <Banknote className="w-8 h-8 text-orange-500" />
                <div>
                  <h3 className="font-semibold text-orange-800">En attente de validation</h3>
                  <p className="text-orange-700 text-sm">
                    Votre demande de paiement en espèces a été envoyée. 
                    Contactez le gérant pour remettre le paiement. 
                    La réservation sera confirmée une fois le paiement validé.
                  </p>
                </div>
              </div>
            </div>
          )}

          {booking.status === 'confirmed' && (
            <div className="card p-6 bg-green-50 border-green-200">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <div>
                  <h3 className="font-semibold text-green-800">Réservation confirmée</h3>
                  <p className="text-green-700 text-sm">
                    {booking.mode_paiement === 'cash' 
                      ? 'Paiement en espèces validé par le gérant'
                      : 'Paiement effectué avec succès'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Récapitulatif prix */}
        <div className="lg:col-span-1">
          <div className="card p-6 sticky top-24">
            <h3 className="font-semibold mb-4">Récapitulatif</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-600">{formatPrice(booking.prix_par_nuit)} FCFA × {booking.nb_nuits} nuits</span>
                <span>{formatPrice(booking.prix_par_nuit * booking.nb_nuits)} FCFA</span>
              </div>
              {booking.frais_menage > 0 && (
                <div className="flex justify-between">
                  <span className="text-neutral-600">Frais de ménage</span>
                  <span>{formatPrice(booking.frais_menage)} FCFA</span>
                </div>
              )}
              <hr />
              <div className="flex justify-between font-semibold text-lg">
                <span>Total</span>
                <span className="text-primary-600">{formatPrice(booking.montant_total)} FCFA</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetail;
